import * as d3 from 'd3';
import React from 'react';

import TimeSeriesProvider from 'src/components/TimeSeriesProvider';
import TimeSeriesViz, {
  STACKED_VIZ_HEIGHT,
  STACKED_VIZ_HEIGHT_WITH_LEGEND,
} from 'src/components/TimeSeriesViz';
import {useTranslations} from 'src/components/TranslationsContext';
import Viz from 'src/components/Viz';
import {
  VizConfig,
  VizConfigCreatorItem,
  filterConfigToAvailableTimeSeries,
  makeConfigItems,
  makeRequestFromVizConfig,
} from 'src/components/Viz/config';
import WithWidth from 'src/components/WithWidth';
import {Site} from 'src/lib/api/project';
import {NDSIHistoricalColumns, NDSIObservationColumns, Source} from 'src/lib/api/series';
import COLORS from 'src/lib/colors';

function makeNdsiConfig(): VizConfig {
  const items: VizConfigCreatorItem[] = [
    {
      displayName: 'Historical 5–95% range',
      source: Source.MODIS_NDSI_HISTORICAL_DAILY,
      columns: [NDSIHistoricalColumns.NDSI_0_05, NDSIHistoricalColumns.NDSI_0_95],
      getStyle: () => ({
        fill: COLORS.SNOW_RANGE,
        fillOpacity: 0.6,
      }),
      legendIcon: 'rect',
      curveFactory: d3.curveMonotoneX,
    },
    {
      displayName: 'Historical mean',
      source: Source.MODIS_NDSI_HISTORICAL_DAILY,
      columns: [NDSIHistoricalColumns.NDSI_MEAN],
      getStyle: () => ({
        stroke: COLORS.SNOW_LTA,
        strokeDasharray: '4 2',
        strokeWidth: 1.5,
      }),
      legendIcon: 'line',
      curveFactory: d3.curveMonotoneX,
    },
    {
      displayName: 'MODIS NDSI',
      source: Source.MODIS_NDSI,
      columns: [NDSIObservationColumns.NDSI_MEDIAN],
      getStyle: () => ({
        stroke: COLORS.OBS,
        strokeWidth: 2,
      }),
      legendIcon: 'line',
      curveFactory: d3.curveMonotoneX,
    },
  ];
  return {
    forecasts: [],
    observations: makeConfigItems('observations', items, {
      timeAggregation: '1D',
      rateVolumeMode: 'rate',
      // MODIS NDSI is a global product, so basin-averaging over all contributing
      // sub-basins works for sites that extend into Canada (unlike SNODAS which
      // is CONUS-only and triggers the zarr "Couldn't find Series" assertion).
      includeFlowContributingAreas: true,
    }),
  };
}

/** Short-label tick formatter — the NDSI chart only spans 6 months, so the
 * year is implicit; showing "Nov" is clearer than "Nov 2025". */
function ndsiTickFormat(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {month: 'short', timeZone}).format(date);
}

/**
 * 6-month basin-averaged MODIS NDSI (snow cover) chart for the regional report.
 * Rendered via the shared TimeSeriesProvider pipeline — matches the Surface.tsx
 * pattern on Site pages.
 */
export default function NdsiChart({site, endDate}: {site: Site; endDate?: string}) {
  useTranslations();

  const dateRange = React.useMemo((): [Date, Date] => {
    const end = endDate ? new Date(`${endDate}T23:59:59Z`) : new Date();
    const start = new Date(end);
    start.setMonth(end.getMonth() - 6);
    return [start, end];
  }, [endDate]);

  const config = React.useMemo(
    () => filterConfigToAvailableTimeSeries(makeNdsiConfig(), site),
    [site]
  );

  if (config.observations.length === 0) {
    return null;
  }

  return (
    <WithWidth>
      {(width) => (
        <TimeSeriesProvider
          site={site}
          forecastTimesBySource={{}}
          dateRange={dateRange}
          {...makeRequestFromVizConfig(config)}
        >
          {(vizProps) => (
            <Viz
              section=""
              title="MODIS basin-average snow cover (NDSI)"
              site={site}
              loadingStateDims={[width, STACKED_VIZ_HEIGHT_WITH_LEGEND]}
              dateRange={dateRange}
              config={config}
              {...vizProps}
            >
              {(props) => (
                <TimeSeriesViz
                  {...props}
                  timeZone={site.localTimeZone}
                  dims={[width, STACKED_VIZ_HEIGHT]}
                  range={[0, 'auto']}
                  xTickFormat={ndsiTickFormat}
                />
              )}
            </Viz>
          )}
        </TimeSeriesProvider>
      )}
    </WithWidth>
  );
}
